"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { GeneralClaimsList, type GeneralClaimFilters } from "./list"
import axiosInstance from "../../../libs/axios"

import { useUrlFilters } from "../../../hooks/useUrlFilters";
import { URL_FILTER_KEYS } from "./list";
import { useDebounce } from "../../../hooks/useDebounce"

const GeneralClaim: React.FC = () => {
  const [claims, setClaims] = useState<any[]>([])
  const [pagination, setPagination] = useState({
    total_count: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  })
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearch = useDebounce(searchTerm, 400)
  // Seeded from the URL so a dashboard tile can open this list already
  // filtered to the rows it counted. URL_FILTER_KEYS lives in ./list.
  const [advancedFilters, setAdvancedFilters] = useState<GeneralClaimFilters>(
    useUrlFilters<GeneralClaimFilters>({}, URL_FILTER_KEYS),
  )
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: "ASC" | "DESC" }>(
    {
      field: "claim_date",
      direction: "DESC",
    }
  )

  useEffect(() => {
    fetchClaims()
  }, [])

  const fetchClaims = async (
    page = pagination.page,
    status = statusFilter,
    search = searchTerm,
    limitValue?: number,
    filters = advancedFilters,
    sort = sortConfig
  ) => {
    try {
      setLoading(true)
      const effectiveLimit = limitValue ?? pagination.limit ?? 10

      const params: any = {
        page: page,
        limit: effectiveLimit,
        ...filters
      }

      if (status) params.status = status
      if (search) params.search = search

      if (sort?.field) {
        params.sort_by = sort.field
        params.sort_order = sort.direction
      }

      const response = await axiosInstance.get("/claims/general/list", { params })

      if (response.data.success) {
        const claimsData = response.data.data.claims || []
        const paginationData = response.data.data.pagination || {
          total_count: 0,
          page: 1,
          limit: 10,
          totalPages: 0,
        }
        
        // Ensure claims is an array
        setClaims(Array.isArray(claimsData) ? claimsData : [])
        setPagination(paginationData)
      } else {
        console.warn("API response not successful:", response.data)
        setClaims([])
      }
    } catch (error) {
      console.error("Error fetching general claims:", error)
      setClaims([])
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (search: string) => {
    setSearchTerm(search)
    // API call triggered by debouncedSearch effect below
  }

  // Fire fetch when debounced search value settles
  useEffect(() => {
    fetchClaims(1, statusFilter, debouncedSearch, undefined, advancedFilters, sortConfig)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const handleFilterChange = (filter: string) => {
    setStatusFilter(filter)
    fetchClaims(1, filter, searchTerm, undefined, advancedFilters, sortConfig)
  }

  const handleAdvancedFilter = (filters: GeneralClaimFilters) => {
    setAdvancedFilters(filters)
    fetchClaims(1, statusFilter, searchTerm, undefined, filters, sortConfig)
  }

  const handlePageChange = (page: number) => {
    fetchClaims(page, statusFilter, searchTerm, undefined, advancedFilters, sortConfig)
  }

  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }))
    fetchClaims(1, statusFilter, searchTerm, limit, advancedFilters, sortConfig)
  }

  const handleSort = (field: string, direction: "ASC" | "DESC") => {
    const sort = { field, direction }
    setSortConfig(sort)
    fetchClaims(pagination.page, statusFilter, searchTerm, undefined, advancedFilters, sort)
  }

  return (
    <div>
      <GeneralClaimsList
        claims={claims}
        pagination={pagination}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
        statusFilter={statusFilter}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onAdvancedFilter={handleAdvancedFilter}
        loading={loading}
        sortConfig={sortConfig}
        onSort={handleSort}
        appliedFilters={advancedFilters}
        searchValue={searchTerm}
        onRefresh={() => fetchClaims(1)}
      />
    </div>
  )
}

export default GeneralClaim
